import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from '../api';
import moment from 'moment';
// DHTMLX Gantt imports
import { gantt } from 'dhtmlx-gantt';
import 'dhtmlx-gantt/codebase/dhtmlxgantt.css';

import { Spin, Alert, Card, Modal, Button, Typography, Radio, Space, Row, Col, Tooltip } from 'antd';
import { UnorderedListOutlined, ZoomInOutlined, ZoomOutOutlined, PlusOutlined, CalendarOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import DueTaskList from './DueTaskList';
import OrderDetailsModal from './OrderDetailsModal';
import AddOrderModal from './AddOrderModal';
import './PatientCalendar.css';

const { Title, Text } = Typography;

const categoryConfig = {
  medications: { color: '#ff6b6b', label: 'Medications', ganttColor: '#ff6b6b' },
  nursing: { color: '#4ecdc4', label: 'Nursing', ganttColor: '#4ecdc4' },
  laboratory: { color: '#45b7d1', label: 'Laboratory', ganttColor: '#45b7d1' },
  radiology: { color: '#96ceb4', label: 'Radiology', ganttColor: '#96ceb4' },
  referral: { color: '#feca57', label: 'Referral', ganttColor: '#feca57' },
  lifestyle: { color: '#ff9ff3', label: 'Lifestyle', ganttColor: '#ff9ff3' },
  vaccine: { color: '#54a0ff', label: 'Vaccine', ganttColor: '#54a0ff' },
  preventive_screening: { color: '#90ee90', label: 'Preventive Screening', ganttColor: '#90ee90' },
  smart_goals: { color: '#f759ab', label: 'SMART Goals', ganttColor: '#f759ab' },
  appointments: { color: '#00d2d3', label: 'Appointments', ganttColor: '#00d2d3' },
  procedures: { color: '#ff7675', label: 'Procedures', ganttColor: '#ff7675' },
  default: { color: '#dddddd', label: 'Other', ganttColor: '#dddddd' }
};

const categorySortOrder = [
  'medications', 'nursing', 'laboratory', 'radiology', 'referral',
  'lifestyle', 'vaccine', 'preventive_screening', 'smart_goals', 'appointments', 'procedures', 'default'
];

const zoomConfig = {
  levels: [
    { name: "year", scale_height: 50, min_column_width: 50, scales: [{ unit: "year", step: 1, format: "%Y" }, { unit: "quarter", step: 1, format: (date) => `Q${Math.floor((date.getMonth() / 3)) + 1}` }] },
    { name: "quarter", scale_height: 50, min_column_width: 90, scales: [{ unit: "quarter", step: 1, format: (date) => `Q${Math.floor((date.getMonth() / 3)) + 1}, ${gantt.date.date_to_str("%Y")(date)}` }, { unit: "month", step: 1, format: "%M" }] },
    { name: "month", scale_height: 50, min_column_width: 120, scales: [{ unit: "month", step: 1, format: "%F, %Y" }, { unit: "week", step: 1, format: (date) => `W${gantt.date.getWeek(date)}` }] },
    { name: "week", scale_height: 50, min_column_width: 80, scales: [{ unit: "week", step: 1, format: (date) => `Week ${gantt.date.getWeek(gantt.date.week_start(new Date(date)))} (${moment(gantt.date.week_start(new Date(date))).format("MMM D")})` }, { unit: "day", step: 1, format: "%d (%D)" }] },
    { name: "days", scale_height: 70, min_column_width: 80, scales: [{ unit: "day", step: 1, format: "%d %M %Y" }, { unit: "hour", step: 4, format: "%H:%i" }] }
  ],
  activeLevel: "week"
};

const PatientCalendar = ({ patientId, refreshTrigger, onOrderCreated }) => {
  const [ordersAndAppointments, setOrdersAndAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTaskStatus, setSelectedTaskStatus] = useState('all');
  const [selectedTaskDetails, setSelectedTaskDetails] = useState(null);
  const [taskDetailsModalVisible, setTaskDetailsModalVisible] = useState(false);
  const [isDueTaskModalVisible, setIsDueTaskModalVisible] = useState(false);
  const [isAddOrderModalVisible, setIsAddOrderModalVisible] = useState(false);
  const [currentZoomLevel, setCurrentZoomLevel] = useState("week");
  const [currentViewDate, setCurrentViewDate] = useState(new Date());
  const navigate = useNavigate();
  const ganttContainerRef = useRef(null);
  const isGanttInitialized = useRef(false);

  // MutationObserver ref to monitor DOM changes and hide project bars
  const mutationObserverRef = useRef(null);
  const [isReadyForGanttInit, setIsReadyForGanttInit] = useState(false);
  const scrollTimeoutRef = useRef(null);

  // Simplified and reliable function to add the 'Today' marker.
  const addTodayMarker = useCallback(() => {
    if (!isGanttInitialized.current || !gantt) {
      return;
    }

    const today = new Date();

    // Always remove the previous marker to prevent duplicates.
    try {
      if (gantt.getMarker("today")) {
        gantt.deleteMarker("today");
      }
    } catch (e) {
      // Ignore if marker doesn't exist or API fails
    }

    // Add the new marker using the plugin's intended method.
    // Styling is now handled entirely by PatientCalendar.css
    gantt.addMarker({
      start_date: today,
      css: "gantt_today_marker",
      text: "", // No text needed for a simple vertical bar
      title: "Today: " + gantt.date.date_to_str(gantt.config.task_date)(today),
      id: "today"
    });
  }, []);

  const fetchEvents = useCallback(async () => {
    if (!patientId) { setLoading(false); setOrdersAndAppointments([]); return; }
    try {
      setLoading(true);
      // Only reset Gantt initialization flags when patientId changes, not during data refresh
      // This prevents unnecessary Gantt destruction during order updates

      console.log("PatientCalendar: Making API call to /orders/patient/" + patientId + "/calendar");
      const response = await axios.get(`/orders/patient/${patientId}/calendar`);
      const events = response.data?.data?.calendarEvents || [];
      console.log("PatientCalendar: API Success - Received", events.length, "events");
      console.log("PatientCalendar: Sample events:", events.slice(0, 3));
      setOrdersAndAppointments(events); setError(null);
    } catch (err) {
      console.error('PatientCalendar: API Error:', err.response?.status, err.response?.data?.message || err.message);
      setError('Failed to load events: ' + (err.response?.data?.message || err.message));
      setOrdersAndAppointments([]);
    } finally { setLoading(false); }
  }, [patientId, refreshTrigger]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // Effect to handle patientId changes - reset Gantt initialization flags
  useEffect(() => {
    // Reset Gantt initialization flags when patientId changes
    // This ensures proper re-initialization for new patients
    setIsReadyForGanttInit(false);
    console.log("PatientCalendar: patientId changed, resetting Gantt initialization flags");
  }, [patientId]);

  // Effect to explicitly destroy Gantt instance when patientId changes
  useEffect(() => {
    // This cleanup function runs BEFORE the effect re-runs due to patientId change,
    // or when the component unmounts.
    return () => {
      try {
        if (isGanttInitialized.current && gantt && typeof gantt.destructor === 'function') {
          console.log("PatientCalendar: patientId changed or unmounting, destroying active Gantt instance.");
          gantt.destructor();
          isGanttInitialized.current = false;
        }
      } catch (error) {
        console.warn("PatientCalendar: Error during patientId cleanup:", error);
        isGanttInitialized.current = false; // Reset flag even if destructor fails
      }
    };
  }, [patientId]); // Key dependency: patientId

  const [dhtmlxTasks, setDhtmlxTasks] = useState({ data: [], links: [] });

  useEffect(() => {
    if (!loading && ganttContainerRef.current) {
      console.log("PatientCalendar: Ready for Gantt Init signal.");
      setIsReadyForGanttInit(true);
    } else {
      // console.log("PatientCalendar: NOT Ready for Gantt Init. Loading:", loading, "Ref:", !!ganttContainerRef.current);
      setIsReadyForGanttInit(false); 
    }
  }, [loading]); 

  useEffect(() => {
    if (loading) return;

    const filteredByStatus = ordersAndAppointments.filter(event => {
      if (selectedTaskStatus === 'all') return true;
      const eventData = event.originalEventDetails || event;
      return (eventData.status || 'Pending').toLowerCase() === selectedTaskStatus.toLowerCase();
    });

    const finalTasksData = [];
    const groupedByCategory = {};

    // 1. Group events by a standardized category key
    filteredByStatus.forEach(event => {
      const details = event.originalEventDetails || event;
      let originalCatKey = details.categoryName || 'default';
      let catKey = originalCatKey.toLowerCase().replace(/ /g, '_');

      // Standardize common variations
      const categoryMap = {
        'medication': 'medications', 'lab': 'laboratory', 'labs': 'laboratory',
        'appointment': 'appointments', 'procedure': 'procedures', 'vaccines': 'vaccine',
        'other_orders': 'default', 'other': 'default'
      };
      catKey = categoryMap[catKey] || catKey;

      if (!categorySortOrder.includes(catKey)) {
        catKey = 'default';
      }

      if (!groupedByCategory[catKey]) {
        groupedByCategory[catKey] = [];
      }
      groupedByCategory[catKey].push(event);
    });

    // 2. Create project headers and child tasks
    categorySortOrder.forEach(categoryKey => {
      const categoryEvents = groupedByCategory[categoryKey];
      if (categoryEvents && categoryEvents.length > 0) {
        // Create the parent "project" task
        finalTasksData.push({
          id: `project_${categoryKey}`,
          text: `📋 ${categoryConfig[categoryKey]?.label || 'Unknown'}`,
          type: "project",
          open: true,
          readonly: true,
          task_class: `gantt-project-category-${categoryKey} gantt-category-header`,
        });

        // Create the child tasks for this category
        categoryEvents.forEach(event => {
          const originalEventDetails = event.originalEventDetails || event;
          const topLevelEvent = event;
          const startDateMoment = moment(topLevelEvent.start || topLevelEvent.createdAt);
          const validStartDate = startDateMoment.isValid() ? startDateMoment.toDate() : new Date();
          
          let endDateMoment = moment(topLevelEvent.end || moment(validStartDate).add(1, 'hour'));
          if (!endDateMoment.isValid() || endDateMoment.isBefore(startDateMoment)) {
            endDateMoment = moment(validStartDate).add(1, 'hour');
          }

          const taskCatKey = categoryKey; // Use the standardized key
          const taskType = taskCatKey === 'preventive_screening' ? "milestone" : "task";
          const taskColor = categoryConfig[taskCatKey]?.ganttColor || categoryConfig.default.ganttColor;

          // Add visual indicators for recurring orders
          let taskText = topLevelEvent.name || 'Unnamed Event';
          let taskClass = `gantt-task-category-${taskCatKey}`;

          if (originalEventDetails.isRecurring) {
            if (originalEventDetails.recurringType === 'continuous') {
              taskText = `🔄 ${taskText}`;
              taskClass += ' gantt-recurring-continuous';
            } else if (originalEventDetails.recurringType === 'interval') {
              taskText = `🔁 ${taskText}`;
              taskClass += ' gantt-recurring-interval';
            }
          }

          finalTasksData.push({
            id: String(topLevelEvent.id || `event-${Math.random().toString(36).substr(2, 9)}`),
            text: taskText,
            parent: `project_${categoryKey}`, // *** This is the critical link ***
            start_date: moment(validStartDate).format("DD-MM-YYYY HH:mm"),
            end_date: endDateMoment.format("DD-MM-YYYY HH:mm"),
            type: taskType,
            color: taskColor,
            task_class: taskClass,
            notes: originalEventDetails.notes || '',
            status_custom: originalEventDetails.status || 'Pending',
            frequency: originalEventDetails.frequency || 'One-time',
            frequencyLabel: originalEventDetails.frequencyLabel || 'One-time',
            isRecurring: originalEventDetails.isRecurring || false,
            instanceNumber: originalEventDetails.instanceNumber,
            _originalEventData: { ...topLevelEvent, originalEventDetails }
          });
        });
      }
    });
    
    // Filter for single category view if needed
    if (selectedCategory !== 'all') {
        const tasksForSelectedCategory = finalTasksData.filter(task => {
            return task.id === `project_${selectedCategory}` || task.parent === `project_${selectedCategory}`;
        });
        setDhtmlxTasks({ data: tasksForSelectedCategory, links: [] });
    } else {
        setDhtmlxTasks({ data: finalTasksData, links: [] });
    }

  }, [ordersAndAppointments, selectedCategory, selectedTaskStatus, loading]);

  useEffect(() => {
    if (!isReadyForGanttInit || isGanttInitialized.current || !ganttContainerRef.current) return;

    console.log("PatientCalendar: Initializing Gantt instance...");

    // Ensure complete cleanup before initialization
    if (ganttContainerRef.current.innerHTML !== "") {
      ganttContainerRef.current.innerHTML = "";
    }

    // Reset gantt to default state before configuration
    try {
      if (gantt && typeof gantt.resetLayout === 'function') {
        gantt.resetLayout();
      }

      // Clear any existing data stores
      if (gantt && typeof gantt.clearAll === 'function') {
        gantt.clearAll();
      }
    } catch (error) {
      console.warn("PatientCalendar: Error during gantt reset:", error);
    }

    // Configure gantt settings
    gantt.config.date_format = "%d-%m-%Y %H:%i";
    gantt.config.xml_date = "%d-%m-%Y %H:%i";
    gantt.config.readonly = true;
    gantt.config.autosize = "y";
    gantt.config.sort = false;

    // Enable tooltips
    gantt.config.tooltip = true;
    gantt.config.tooltip_timeout = 30;
    gantt.config.tooltip_hide_timeout = 5000;

    // Ensure project tasks are displayed
    gantt.config.show_task_cells = true;
    gantt.config.show_grid = true;
    gantt.config.show_chart = true;
    gantt.config.open_tree_initially = true;
    gantt.config.auto_types = true;
    gantt.config.show_progress = false;
    gantt.config.show_links = false;

    // Define task types explicitly
    gantt.config.types = {
      task: "task",
      project: "project",
      milestone: "milestone"
    };

    // Disable automatic project task timeline rendering to prevent unwanted category header bars
    // Project tasks (category headers) should only display text, not timeline bars
    gantt.config.project_start = null;
    gantt.config.project_end = null;
    gantt.config.auto_types = false; // Disable automatic type detection
    gantt.config.type_renderers = gantt.config.type_renderers || {};

    // Custom renderer for project tasks to prevent bar rendering
    gantt.config.type_renderers.project = function(task) {
      return ""; // Return empty string to prevent any bar rendering
    };

    // Enable horizontal scrolling
    gantt.config.scroll_on_load = true;
    gantt.config.horizontal_scroll_key = "shiftKey";
    gantt.config.wheel_scroll_sensitivity = 0.4;

    // Configure today marker
    gantt.config.show_markers = true;
    gantt.config.marker_line_height = "100%";

    gantt.config.columns = [
      {
        name: "text",
        label: "Task/Event Name",
        tree: true,
        width: '*',
        resize: false,
        template: (task) => {
          // Special handling for project headers (category headers)
          if (task.type === "project") {
            return `<div class="gantt-category-header-text">${task.text}</div>`;
          }
          // For regular tasks, make them clickable with a cursor pointer
          return `<div class="gantt-task-name-clickable" style="cursor: pointer;" data-task-id="${task.id}">${task.text}</div>`;
        }
      }
    ];
    gantt.config.lightbox.sections = [
      { name: "description", height: 70, map_to: "text", type: "template", label: "Name/Description" },
      { name: "status_custom_display", height: 30, map_to: "status_custom", type: "template", label: "Status" },
      { name: "notes_display", height: 70, map_to: "notes", type: "template", label: "Notes" },
      { name: "time", type: "time", map_to: "auto", readonly:true, label: "Time Period" }
    ];
    gantt.templates.task_class = (start, end, task) => task.task_class || (task.type === "project" ? "gantt_project_task" : "");

    // Custom template to prevent project task bar rendering and handle text truncation
    gantt.templates.task_text = (start, end, task) => {
      // For project tasks (category headers), return empty string to prevent bar content
      if (task.type === "project") {
        return "";
      }

      // Calculate task duration in minutes to determine display strategy
      const startDate = new Date(start);
      const endDate = new Date(end);
      const durationMinutes = (endDate - startDate) / (1000 * 60);
      const text = task.text || '';

      // Define duration thresholds for different text display strategies
      const VERY_SHORT_DURATION = 30; // 30 minutes or less
      const SHORT_DURATION = 120; // 2 hours or less
      const MEDIUM_DURATION = 480; // 8 hours or less

      if (durationMinutes <= VERY_SHORT_DURATION) {
        // Very short tasks: show only a small indicator dot
        return '<span class="task-indicator-dot">•</span>';
      } else if (durationMinutes <= SHORT_DURATION) {
        // Short tasks: show abbreviated text (first word + ellipsis)
        const firstWord = text.split(' ')[0];
        return firstWord.length > 8 ? firstWord.substring(0, 6) + '...' : firstWord;
      } else if (durationMinutes <= MEDIUM_DURATION) {
        // Medium tasks: show truncated text with ellipsis
        const maxLength = Math.min(20, text.length); // Limit to reasonable length
        return text.length > maxLength ? text.substring(0, maxLength - 3) + '...' : text;
      } else {
        // Long tasks: show full text (will be handled by CSS ellipsis if needed)
        return text;
      }
    };

    // Additional template to completely hide project task bars
    gantt.templates.task_row_class = function(start, end, task) {
      if (task.type === "project") {
        return "gantt_project_row_hidden";
      }
      return "";
    };

    // Override the task rendering for project tasks and add minimum width class
    gantt.templates.task_class = function(start, end, task) {
      if (task.type === "project") {
        return "gantt_project_hidden";
      }

      // Add minimum width class for very short duration tasks
      const startDate = new Date(start);
      const endDate = new Date(end);
      const durationMinutes = (endDate - startDate) / (1000 * 60);

      let classes = task.task_class || "";

      // Add minimum width class for tasks shorter than 2 hours
      if (durationMinutes <= 120) {
        classes += " gantt-task-minimum-width";
      }

      // Add duration-based classes for styling
      if (durationMinutes <= 30) {
        classes += " gantt-task-very-short";
      } else if (durationMinutes <= 120) {
        classes += " gantt-task-short";
      }

      return classes.trim();
    };

    // Custom template for task bar rendering to handle minimum width
    gantt.templates.task_bar_text = function(start, end, task) {
      if (task.type === "project") {
        return "";
      }

      // This template is called when rendering the task bar itself
      // We'll use CSS to handle the minimum width, but this ensures proper text positioning
      return gantt.templates.task_text(start, end, task);
    };

    // DISABLED: Cell-based today markers to prevent multiple marker display
    // Using priority system: DHTMLX native marker plugin is primary method
    // gantt.templates.scale_cell_class = function(date) {
    //   const today = new Date();
    //   const cellDate = new Date(date);
    //   if (cellDate.toDateString() === today.toDateString()) {
    //     return "gantt_today_scale_cell";
    //   }
    //   return "";
    // };

    // gantt.templates.task_cell_class = function(item, date) {
    //   const today = new Date();
    //   const cellDate = new Date(date);
    //   if (cellDate.toDateString() === today.toDateString()) {
    //     return "gantt_today_task_cell";
    //   }
    //   return "";
    // };

    gantt.templates.tooltip_text = (start, end, task) => {
      if (task.type === 'project') {
        return `<b>${task.text}</b>`;
      }

      // Enhanced tooltip with detailed order information
      const originalData = task._originalEventData;
      const eventDetails = originalData?.originalEventDetails || originalData || {};

      const startDate = moment(start).format('MMM D, YYYY HH:mm');
      const endDate = moment(end).format('MMM D, YYYY HH:mm');

      let tooltip = `<div style="max-width: 300px; line-height: 1.4;">`;
      tooltip += `<div style="font-weight: bold; font-size: 14px; margin-bottom: 8px; color: #1890ff;">${task.text}</div>`;

      // Date information
      if (startDate === endDate || moment(start).format('MMM D, YYYY') === moment(end).format('MMM D, YYYY')) {
        tooltip += `<div style="margin-bottom: 6px;"><b>Date:</b> ${moment(start).format('MMM D, YYYY')}</div>`;
        if (moment(start).format('HH:mm') !== '00:00') {
          tooltip += `<div style="margin-bottom: 6px;"><b>Time:</b> ${moment(start).format('HH:mm')}</div>`;
        }
      } else {
        tooltip += `<div style="margin-bottom: 6px;"><b>Start:</b> ${startDate}</div>`;
        tooltip += `<div style="margin-bottom: 6px;"><b>End:</b> ${endDate}</div>`;
      }

      // Status with color coding
      const status = task.status_custom || eventDetails.status || 'Pending';
      const statusColor = status.toLowerCase() === 'completed' ? '#52c41a' :
                         status.toLowerCase() === 'active' ? '#1890ff' :
                         status.toLowerCase() === 'cancelled' ? '#ff4d4f' :
                         status.toLowerCase() === 'resulted' ? '#722ed1' : '#faad14';
      tooltip += `<div style="margin-bottom: 6px;"><b>Status:</b> <span style="color: ${statusColor}; font-weight: 500;">${status}</span></div>`;

      // Category
      const category = eventDetails.categoryName || 'Other';
      const categoryColor = categoryConfig[category.toLowerCase().replace(/ /g, '_')]?.color || categoryConfig.default.color;
      tooltip += `<div style="margin-bottom: 6px;"><b>Category:</b> <span style="color: ${categoryColor}; font-weight: 500;">${category}</span></div>`;

      // Order type/description
      if (eventDetails.orderType) {
        tooltip += `<div style="margin-bottom: 6px;"><b>Type:</b> ${eventDetails.orderType}</div>`;
      }

      // Description
      if (eventDetails.description && eventDetails.description !== task.text) {
        const truncatedDesc = eventDetails.description.length > 100 ?
          eventDetails.description.substring(0, 100) + '...' : eventDetails.description;
        tooltip += `<div style="margin-bottom: 6px;"><b>Description:</b> ${truncatedDesc}</div>`;
      }

      // Notes
      if (task.notes && task.notes.trim()) {
        const truncatedNotes = task.notes.length > 80 ?
          task.notes.substring(0, 80) + '...' : task.notes;
        tooltip += `<div style="margin-bottom: 6px;"><b>Notes:</b> <em>${truncatedNotes}</em></div>`;
      }

      // Provider/Ordered by
      if (eventDetails.orderedBy || eventDetails.provider) {
        tooltip += `<div style="margin-bottom: 6px;"><b>Provider:</b> ${eventDetails.orderedBy || eventDetails.provider}</div>`;
      }

      // Priority
      if (eventDetails.priority && eventDetails.priority !== 'Normal') {
        const priorityColor = eventDetails.priority.toLowerCase() === 'high' ? '#ff4d4f' :
                             eventDetails.priority.toLowerCase() === 'urgent' ? '#ff7875' : '#1890ff';
        tooltip += `<div style="margin-bottom: 6px;"><b>Priority:</b> <span style="color: ${priorityColor}; font-weight: 500;">${eventDetails.priority}</span></div>`;
      }

      // Location
      if (eventDetails.location) {
        tooltip += `<div style="margin-bottom: 6px;"><b>Location:</b> ${eventDetails.location}</div>`;
      }

      tooltip += `<div style="margin-top: 8px; font-size: 11px; color: #666; font-style: italic;">Click for full details</div>`;
      tooltip += `</div>`;

      return tooltip;
    };

    // Configure scroll behavior
    gantt.config.scroll_on_load = true;



    // Initialize plugins before gantt.init()
    try {
      gantt.plugins({ context_menu: true, zoom: true, marker: true, tooltip: true });
    } catch (error) {
      console.warn("PatientCalendar: Error initializing plugins:", error);
    }

    // Initialize gantt with error handling
    try {
      gantt.init(ganttContainerRef.current);
      console.log("PatientCalendar: gantt.init() called successfully.");
      isGanttInitialized.current = true;

      // Setup MutationObserver to monitor for project task bars
      setupProjectBarObserver();

      // Add marker with single call to prevent duplicates
      addTodayMarker();

      // Add scroll event listener to update current view date with debouncing
      gantt.attachEvent("onScroll", function(left, top) {
        if (isGanttInitialized.current) {
          // Clear previous timeout
          if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
          }

          // Set new timeout for debounced update
          scrollTimeoutRef.current = setTimeout(() => {
            const visibleDate = gantt.dateFromPos(left + gantt.config.task_scroll_offset || 0);
            if (visibleDate) {
              setCurrentViewDate(new Date(visibleDate));
            }
          }, 100);
        }
      });

    } catch (error) {
      console.error("PatientCalendar: Error during gantt.init():", error);
      isGanttInitialized.current = false;
      return; // Exit early if initialization failed
    }

    // Initialize zoom extension with error handling
    try {
      if (gantt.ext && gantt.ext.zoom) {
        gantt.ext.zoom.init(zoomConfig);
        gantt.ext.zoom.setLevel(zoomConfig.activeLevel || "week");
      } else {
        console.warn("DHTMLX Zoom extension not available.");
      }
    } catch (error) {
      console.warn("PatientCalendar: Error initializing zoom:", error);
    }

    // Set a very large, fixed date range for "infinite" scrolling
    const today = new Date();
    const startDate = new Date(today);
    startDate.setFullYear(startDate.getFullYear() - 10);
    const endDate = new Date(today);
    endDate.setFullYear(endDate.getFullYear() + 10);

    gantt.config.start_date = startDate;
    gantt.config.end_date = endDate;

    // Add event listener for zoom changes to maintain today focus and track zoom level
    if (gantt.ext && gantt.ext.zoom) {
      gantt.ext.zoom.attachEvent("onAfterZoom", function(level, config) {
        setCurrentZoomLevel(config.name);
        setTimeout(() => {
          if (isGanttInitialized.current) {
            const today = new Date();
            gantt.showDate(today);
            // Update task bar visibility after zoom change
            updateTaskBarVisibility();
            setCurrentViewDate(today);

            // Refresh today marker for new zoom level using consistent method
            console.log("PatientCalendar: Refreshing today marker after zoom change to:", config.name);
            addTodayMarker();

            // Force refresh after zoom change
            gantt.refreshData();
          }
        }, 100);
      });
    }

    gantt.config.context_menu = [ /* ... */ ];

    // This cleanup is for the main init effect, runs if isReadyForGanttInit changes or component unmounts
    return () => {
      console.log("PatientCalendar: Cleaning up Gantt from main init effect.");

      // Remove task name click event listeners
      if (ganttContainerRef.current) {
        ganttContainerRef.current.removeEventListener('click', handleTaskNameClick);
      }

      // The destructor specific to patientId change is handled by the other useEffect
      // This one handles general unmount or if isReadyForGanttInit becomes false.
      try {
        if (isGanttInitialized.current && gantt && typeof gantt.destructor === 'function') {
          gantt.destructor();
        }
      } catch (error) {
        console.warn("PatientCalendar: Error during gantt cleanup:", error);
      }
      isGanttInitialized.current = false;
    };
  }, [isReadyForGanttInit]);

  useEffect(() => {
    if (isGanttInitialized.current && isReadyForGanttInit && !loading && ganttContainerRef.current) {
      console.log("PatientCalendar: Parsing data. Tasks count:", dhtmlxTasks.data.length);

      // Debug: Log the data structure being parsed
      const projectTasks = dhtmlxTasks.data.filter(task => task.type === "project");
      console.log("Project tasks being parsed:", projectTasks.length, projectTasks);

      try {
        gantt.clearAll();
        gantt.parse(dhtmlxTasks);

        // Debug: Check what gantt actually loaded
        console.log("Gantt loaded tasks:", gantt.getTaskCount());
        const loadedProjects = [];
        gantt.eachTask(function(task) {
          if (task.type === "project") {
            loadedProjects.push({id: task.id, text: task.text, type: task.type});
          }
        });
        console.log("Loaded project tasks in gantt:", loadedProjects);

        // Center timeline on today's date after data is loaded
        setTimeout(() => {
          try {
            centerTimelineOnToday();
            // Aggressively hide project task bars with multiple attempts
            updateTaskBarVisibility();

            // Ensure today marker is visible after data load
            addTodayMarker();

            gantt.refreshData();
            console.log("PatientCalendar: Today marker refreshed after data load");
          } catch (postParseError) {
            console.warn("PatientCalendar: Error in post-parse operations:", postParseError);
            // Continue execution even if post-parse operations fail
          }
        }, 50);

        // Additional attempts to hide project bars at different intervals
        setTimeout(updateTaskBarVisibility, 100);
        setTimeout(updateTaskBarVisibility, 200);
        setTimeout(updateTaskBarVisibility, 500);



        // Attach click event handler after data is loaded
        gantt.attachEvent("onTaskClick", function(id, e) {
          const task = gantt.getTask(id);
          // If it's a regular task with event data, show the modal
          if (task.type !== "project" && task._originalEventData) {
            setSelectedTaskDetails(task._originalEventData);
            setTaskDetailsModalVisible(true);
            return false; // Prevent default lightbox for tasks
          }
          // If it's a project (category header), allow the default expand/collapse
          return true;
        });

        // Add click event listener for task names in the grid column
        const addTaskNameClickListeners = () => {
          // Use event delegation to handle clicks on task names
          const ganttContainer = ganttContainerRef.current;
          if (!ganttContainer) return;

          // Remove any existing listeners to prevent duplicates
          ganttContainer.removeEventListener('click', handleTaskNameClick);

          // Add the new listener
          ganttContainer.addEventListener('click', handleTaskNameClick);
        };

        // Set up the task name click listeners
        addTaskNameClickListeners();

      } catch (error) {
        console.error("PatientCalendar: Error parsing gantt data:", error);

        // Enhanced error recovery with multiple fallback strategies
        try {
          // First attempt: Clear and retry with current data
          console.log("PatientCalendar: Attempting error recovery - clearing and retrying");
          gantt.clearAll();

          // Wait a bit and try again with simplified data
          setTimeout(() => {
            try {
              gantt.parse(dhtmlxTasks);
              console.log("PatientCalendar: Error recovery successful");
            } catch (retryError) {
              console.error("PatientCalendar: Retry failed, forcing re-initialization:", retryError);
              // Last resort: Force complete re-initialization
              isGanttInitialized.current = false;
              setIsReadyForGanttInit(false);
              setTimeout(() => {
                setIsReadyForGanttInit(true);
              }, 200);
            }
          }, 100);
        } catch (recoveryError) {
          console.error("PatientCalendar: Error recovery failed:", recoveryError);
          // Force re-initialization as last resort
          isGanttInitialized.current = false;
          setIsReadyForGanttInit(false);
          setTimeout(() => {
            setIsReadyForGanttInit(true);
          }, 200);
        }
      }
    }
  }, [dhtmlxTasks, isReadyForGanttInit, loading]);

  // Real-time today indicator update
  useEffect(() => {
    if (!isGanttInitialized.current) return;

    const updateTodayIndicator = () => {
      if (isGanttInitialized.current) {
        gantt.refreshData();
      }
    };

    // Update today indicator at midnight
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    const msUntilMidnight = tomorrow.getTime() - now.getTime();

    // Set timeout for midnight update
    const midnightTimeout = setTimeout(() => {
      updateTodayIndicator();

      // Set up daily interval after first midnight update
      const dailyInterval = setInterval(updateTodayIndicator, 24 * 60 * 60 * 1000);

      return () => clearInterval(dailyInterval);
    }, msUntilMidnight);

    return () => {
      clearTimeout(midnightTimeout);
    };
  }, [isGanttInitialized.current]);

  // Keyboard navigation support
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only handle keyboard events when the gantt container is focused or when no input is focused
      const activeElement = document.activeElement;
      const isInputFocused = activeElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.contentEditable === 'true'
      );

      if (isInputFocused) return;

      if (event.key === 'ArrowLeft' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        handleScrollLeft();
      } else if (event.key === 'ArrowRight' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        handleScrollRight();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentViewDate, currentZoomLevel]);

  const handleZoomIn = () => { if (isGanttInitialized.current && gantt.ext?.zoom) gantt.ext.zoom.zoomOut(); };
  const handleZoomOut = () => { if (isGanttInitialized.current && gantt.ext?.zoom) gantt.ext.zoom.zoomIn(); };
  const handleTaskDetailsModalClose = () => { setSelectedTaskDetails(null); setTaskDetailsModalVisible(false); };
  const handleNavigateToOrder = (eventId, eventType) => { /* ... */ };
  const handleAddOrderClick = () => { setIsAddOrderModalVisible(true); };
  const handleAddOrderModalClose = () => { setIsAddOrderModalVisible(false); };
  const handleOrderCreated = () => {
    console.log("PatientCalendar: Order created, refreshing timeline data");

    // Refresh the timeline data when a new order is created
    // The fetchEvents function now properly handles data refresh without destroying Gantt
    try {
      fetchEvents();
      // Also trigger the parent's order change handler if provided
      if (onOrderCreated) onOrderCreated();
    } catch (error) {
      console.error("PatientCalendar: Error during order creation refresh:", error);
      // If refresh fails, show error but don't break the UI
      setError('Failed to refresh timeline after order creation. Please refresh the page.');
    }
  };

  // AGGRESSIVE function to permanently hide project task bars (category headers)
  // Uses multiple strategies to ensure category headers show NO timeline bars
  const updateTaskBarVisibility = () => {
    if (!isGanttInitialized.current) return;

    try {
      // Strategy 1: Hide all project task lines with maximum force
      const projectTaskSelectors = [
        '.gantt_task_line.gantt_project',
        '.gantt_task_line.gantt_project_task',
        '[class*="gantt_task_line"][class*="gantt_project"]'
      ];

      projectTaskSelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(taskElement => {
          // Apply maximum hiding styles
          taskElement.style.setProperty('display', 'none', 'important');
          taskElement.style.setProperty('visibility', 'hidden', 'important');
          taskElement.style.setProperty('opacity', '0', 'important');
          taskElement.style.setProperty('width', '0', 'important');
          taskElement.style.setProperty('height', '0', 'important');
          taskElement.style.setProperty('max-width', '0', 'important');
          taskElement.style.setProperty('max-height', '0', 'important');
          taskElement.style.setProperty('transform', 'scale(0)', 'important');
          taskElement.style.setProperty('overflow', 'hidden', 'important');
          taskElement.style.setProperty('background', 'transparent', 'important');
          taskElement.style.setProperty('border', 'none', 'important');

          // Also hide any content within
          const content = taskElement.querySelector('.gantt_task_content');
          if (content) {
            content.style.setProperty('display', 'none', 'important');
            content.style.setProperty('visibility', 'hidden', 'important');
            content.style.setProperty('opacity', '0', 'important');
          }
        });
      });

      // Strategy 2: Target category-specific project tasks
      const categoryClasses = [
        'gantt-project-category-medications',
        'gantt-project-category-nursing',
        'gantt-project-category-laboratory',
        'gantt-project-category-radiology',
        'gantt-project-category-referral',
        'gantt-project-category-lifestyle',
        'gantt-project-category-vaccine',
        'gantt-project-category-preventive_screening',
        'gantt-project-category-smart_goals',
        'gantt-project-category-appointments',
        'gantt-project-category-procedures',
        'gantt-project-category-default'
      ];

      categoryClasses.forEach(categoryClass => {
        const categoryRows = document.querySelectorAll(`.${categoryClass}`);
        categoryRows.forEach(row => {
          const projectBars = row.querySelectorAll('.gantt_task_line');
          projectBars.forEach(bar => {
            bar.style.setProperty('display', 'none', 'important');
            bar.style.setProperty('visibility', 'hidden', 'important');
            bar.style.setProperty('opacity', '0', 'important');
          });
        });
      });

      console.log('PatientCalendar: Aggressively hidden all project task bars');
    } catch (error) {
      console.warn('PatientCalendar: Error hiding project task bars:', error);
    }
  };

  // Setup MutationObserver to continuously monitor and hide project task bars
  const setupProjectBarObserver = () => {
    if (!ganttContainerRef.current || mutationObserverRef.current) return;

    try {
      mutationObserverRef.current = new MutationObserver((mutations) => {
        let shouldHideBars = false;

        mutations.forEach((mutation) => {
          // Check if new nodes were added that might be project task bars
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                const element = node;
                // Check if the added element is or contains project task bars
                if (element.classList?.contains('gantt_task_line') ||
                    element.querySelector?.('.gantt_task_line.gantt_project')) {
                  shouldHideBars = true;
                }
              }
            });
          }

          // Check if attributes changed that might affect visibility
          if (mutation.type === 'attributes' &&
              (mutation.attributeName === 'style' || mutation.attributeName === 'class')) {
            const target = mutation.target;
            if (target.classList?.contains('gantt_task_line') &&
                target.classList?.contains('gantt_project')) {
              shouldHideBars = true;
            }
          }
        });

        // If we detected changes that might affect project bars, hide them
        if (shouldHideBars) {
          setTimeout(updateTaskBarVisibility, 10);
        }
      });

      // Start observing the gantt container
      mutationObserverRef.current.observe(ganttContainerRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class']
      });

      console.log('PatientCalendar: MutationObserver setup for project bar monitoring');
    } catch (error) {
      console.warn('PatientCalendar: Error setting up MutationObserver:', error);
    }
  };

  // Cleanup MutationObserver
  const cleanupProjectBarObserver = () => {
    if (mutationObserverRef.current) {
      mutationObserverRef.current.disconnect();
      mutationObserverRef.current = null;
    }
  };

  // Ensure project task bars remain hidden after any Gantt re-renders
  useEffect(() => {
    if (isGanttInitialized.current) {
      // Use setTimeout to ensure DOM is updated after Gantt re-renders
      setTimeout(updateTaskBarVisibility, 100);
    }
  }, [selectedCategory, dhtmlxTasks]);

  // Cleanup MutationObserver on component unmount
  useEffect(() => {
    return () => {
      cleanupProjectBarObserver();
    };
  }, []);

  const handleExpandAll = () => {
    if (!isGanttInitialized.current) return;
    gantt.eachTask((task) => {
      if (gantt.isTaskExists(task.id) && task.type === 'project') {
        gantt.open(task.id);
      }
    });
  };

  const handleCollapseAll = () => {
    if (!isGanttInitialized.current) return;
    gantt.eachTask((task) => {
      if (gantt.isTaskExists(task.id) && task.type === 'project') {
        gantt.close(task.id);
      }
    });
  };

  const handleGoToToday = () => {
    if (isGanttInitialized.current) {
      const today = new Date();
      gantt.showDate(today);
      setCurrentViewDate(today);
      // The marker is already present, no need to re-add it,
      // as it's a static element whose position is calculated by gantt.
    }
  };

  // Function to center timeline on today's date
  const centerTimelineOnToday = () => {
    if (isGanttInitialized.current) {
      const today = new Date();
      gantt.showDate(today);
      setCurrentViewDate(today);
    }
  };

  // Function to focus on a specific task with visual feedback
  const focusOnTask = (taskId) => {
    if (!isGanttInitialized.current || !gantt.isTaskExists(taskId)) {
      console.warn(`PatientCalendar: Task ${taskId} does not exist or gantt not initialized`);
      return;
    }

    try {
      // First, scroll to make the task visible in the timeline
      gantt.showTask(taskId);

      // Provide visual feedback by selecting the task temporarily
      gantt.selectTask(taskId);

      // Update the current view date to the task's start date for better context
      const task = gantt.getTask(taskId);
      if (task && task.start_date) {
        setCurrentViewDate(new Date(task.start_date));
      }

      // Remove the selection after a brief delay to provide temporary highlight
      setTimeout(() => {
        if (isGanttInitialized.current && gantt.isTaskExists(taskId)) {
          gantt.unselectTask(taskId);
        }
      }, 1500); // 1.5 seconds highlight duration

      console.log(`PatientCalendar: Focused on task ${taskId}`);
    } catch (error) {
      console.error(`PatientCalendar: Error focusing on task ${taskId}:`, error);
    }
  };

  // Handler for task name clicks - defined outside useEffect for cleanup access
  const handleTaskNameClick = useCallback((event) => {
    // Check if the clicked element is a task name
    const taskNameElement = event.target.closest('.gantt-task-name-clickable');
    if (!taskNameElement) return;

    const taskId = taskNameElement.getAttribute('data-task-id');
    if (taskId) {
      // Prevent the default gantt click behavior for this specific click
      event.stopPropagation();
      event.preventDefault();

      // Focus on the task timeline
      focusOnTask(taskId);
    }
  }, []);



  // Horizontal navigation functions
  const handleScrollLeft = () => {
    if (isGanttInitialized.current) {
      const newDate = new Date(currentViewDate);

      // Navigate based on current zoom level
      switch (currentZoomLevel) {
        case "week":
          newDate.setDate(newDate.getDate() - 7);
          break;
        case "month":
          newDate.setMonth(newDate.getMonth() - 1);
          break;
        case "quarter":
          newDate.setMonth(newDate.getMonth() - 3);
          break;
        case "year":
          newDate.setFullYear(newDate.getFullYear() - 1);
          break;
        default:
          newDate.setDate(newDate.getDate() - 7);
      }

      gantt.showDate(newDate);
      setCurrentViewDate(newDate);
    }
  };

  const handleScrollRight = () => {
    if (isGanttInitialized.current) {
      const newDate = new Date(currentViewDate);

      // Navigate based on current zoom level
      switch (currentZoomLevel) {
        case "week":
          newDate.setDate(newDate.getDate() + 7);
          break;
        case "month":
          newDate.setMonth(newDate.getMonth() + 1);
          break;
        case "quarter":
          newDate.setMonth(newDate.getMonth() + 3);
          break;
        case "year":
          newDate.setFullYear(newDate.getFullYear() + 1);
          break;
        default:
          newDate.setDate(newDate.getDate() + 7);
      }

      gantt.showDate(newDate);
      setCurrentViewDate(newDate);
    }
  };

  // Function to get current period display text
  const getCurrentPeriodText = () => {
    const date = currentViewDate;
    switch (currentZoomLevel) {
      case "week":
        const weekStart = moment(date).startOf('week');
        const weekEnd = moment(date).endOf('week');
        return `${weekStart.format('MMM D')} - ${weekEnd.format('MMM D, YYYY')}`;
      case "month":
        return moment(date).format('MMMM YYYY');
      case "quarter":
        const quarter = Math.floor(moment(date).month() / 3) + 1;
        return `Q${quarter} ${moment(date).format('YYYY')}`;
      case "year":
        return moment(date).format('YYYY');
      default:
        return moment(date).format('MMMM YYYY');
    }
  };

  if (loading && !isReadyForGanttInit) return <Card><div style={{ textAlign: 'center', padding: '50px' }}><Spin size="large" /><div style={{ marginTop: '16px' }}>Loading Calendar Data...</div></div></Card>;
  if (error) return <Card><Alert message="Error" description={error} type="error" showIcon action={<Button size="small" onClick={fetchEvents}>Retry</Button>} /></Card>;

  return (
    <div className={`patient-calendar ${selectedCategory !== 'all' ? 'single-category-view' : ''}`}>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={24}>
          <Card style={{ padding: '24px' }} title={
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <Title level={4} style={{ margin: 0 }}>Patient Care Timeline</Title>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Text type="secondary" style={{ fontSize: '14px', fontWeight: '500' }}>
                    Current View: {getCurrentPeriodText()}
                  </Text>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                <Radio.Group value={selectedTaskStatus} onChange={(e) => setSelectedTaskStatus(e.target.value)} size="small">
                  <Radio.Button value="all">All Statuses</Radio.Button>
                  <Radio.Button value="Pending">Pending</Radio.Button>
                  <Radio.Button value="Active">Active</Radio.Button>
                  <Radio.Button value="Completed">Completed</Radio.Button>
                  <Radio.Button value="Cancelled">Cancelled</Radio.Button>
                  <Radio.Button value="Resulted">Resulted</Radio.Button>
                </Radio.Group>
                <Space align="center" className="timeline-nav-buttons">
                  <Tooltip title="Add New Order">
                    <Button icon={<PlusOutlined />} onClick={handleAddOrderClick} size="small" type="primary" />
                  </Tooltip>
                  <Button icon={<UnorderedListOutlined />} onClick={() => setIsDueTaskModalVisible(true)} size="small" title="View Due Tasks" />
                  <Tooltip title="Expand All">
                    <Button icon={<ZoomInOutlined />} onClick={handleExpandAll} size="small" />
                  </Tooltip>
                  <Tooltip title="Collapse All">
                    <Button icon={<ZoomOutOutlined />} onClick={handleCollapseAll} size="small" />
                  </Tooltip>
                  <Tooltip title="Go to Today">
                    <Button icon={<CalendarOutlined />} onClick={handleGoToToday} size="small" />
                  </Tooltip>
                  <div style={{ borderLeft: '1px solid #d9d9d9', height: '20px', margin: '0 4px' }} />
                  <Tooltip title={`Previous ${currentZoomLevel === 'week' ? 'Week' : currentZoomLevel === 'month' ? 'Month' : currentZoomLevel === 'quarter' ? 'Quarter' : 'Year'} (Ctrl+←)`}>
                    <Button icon={<LeftOutlined />} onClick={handleScrollLeft} size="small" />
                  </Tooltip>
                  <Tooltip title={`Next ${currentZoomLevel === 'week' ? 'Week' : currentZoomLevel === 'month' ? 'Month' : currentZoomLevel === 'quarter' ? 'Quarter' : 'Year'} (Ctrl+→)`}>
                    <Button icon={<RightOutlined />} onClick={handleScrollRight} size="small" />
                  </Tooltip>
                  <div style={{ borderLeft: '1px solid #d9d9d9', height: '20px', margin: '0 4px' }} />
                  <Tooltip title="Zoom In">
                    <Button icon={<ZoomInOutlined />} onClick={handleZoomIn} size="small" />
                  </Tooltip>
                  <Tooltip title="Zoom Out">
                    <Button icon={<ZoomOutOutlined />} onClick={handleZoomOut} size="small" />
                  </Tooltip>
                </Space>
              </div>
              <div style={{ marginTop: '2px' }}>
                <Radio.Group value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} size="small">
                  <Radio.Button value="all">All Categories</Radio.Button>
                  {Object.entries(categoryConfig).map(([key, config]) => (
                    key !== 'default' && (
                      <Radio.Button key={key} value={key}>
                        <span style={{ color: config.color, marginRight: '5px' }}>●</span>
                        {config.label}
                      </Radio.Button>
                    )
                  ))}
                </Radio.Group>
              </div>
            </div>
          } >
            <div ref={ganttContainerRef} style={{ width: '100%', height: '550px' }}></div>
            {dhtmlxTasks.data.length === 0 && !loading && (
              <div style={{ textAlign: 'center', padding: '50px', color: '#999' }}>
                <Text>No scheduled events to display for the selected criteria.</Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>
      <Modal title="Due Tasks (Next 30 Days)" open={isDueTaskModalVisible} onCancel={() => setIsDueTaskModalVisible(false)} footer={ [<Button key="close" onClick={() => setIsDueTaskModalVisible(false)}>Close</Button>]} width={600} > <DueTaskList tasks={ordersAndAppointments} onOrderClick={(orderId) => { navigate(`/orders/${orderId}`); setIsDueTaskModalVisible(false); }} categoryConfig={categoryConfig} /> </Modal>
      <OrderDetailsModal
        visible={taskDetailsModalVisible}
        onClose={handleTaskDetailsModalClose}
        orderData={selectedTaskDetails}
        categoryConfig={categoryConfig}
      />

      <AddOrderModal
        visible={isAddOrderModalVisible}
        onClose={handleAddOrderModalClose}
        onOrderCreated={handleOrderCreated}
        patientId={patientId}
        title="Add New Order - Timeline"
      />
    </div>
  );
};

export default PatientCalendar;
